import React, { useState } from 'react';
import { ChevronLeft, Copy, Volume2, FileDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Summary } from '@/features/summaries/types';
import { useToast } from '@/hooks/use-toast';

interface MarkdownSummaryViewerProps {
  summary: Summary;
  onBack: () => void;
}

export const MarkdownSummaryViewer: React.FC<MarkdownSummaryViewerProps> = ({ summary, onBack }) => {
  const [copiedText, setCopiedText] = useState('');
  const { toast } = useToast();

  const handleCopy = () => {
    navigator.clipboard.writeText(summary.content || '');
    setCopiedText('Copied!');
    setTimeout(() => setCopiedText(''), 2000);
  };

  const handleAudio = () => {
    toast({
      title: "Coming soon",
      description: "Text-to-speech will be available soon.",
    });
  };

  const handleSaveNote = () => {
    toast({
      title: "Coming soon",
      description: "Save as note will be available soon.",
    });
  };

  const handleDownload = () => {
    const blob = new Blob([summary.content || ''], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${summary.title}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Back Button - Sticky */}
      <div className="sticky top-0 z-10 shrink-0 bg-background px-4 pb-2 pt-4">
        <button
          onClick={onBack}
          className="flex h-fit w-fit items-center gap-2 rounded-lg bg-transparent p-0 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="text-sm">Back</span>
        </button>
      </div>

      {/* Content Area */}
      <div className="relative flex-1 overflow-y-auto">
      <div className="px-4">
          <div className="prose prose-neutral dark:prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground/90 prose-strong:text-foreground prose-li:text-foreground/90">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <h1 className="text-xl font-bold mb-4 text-foreground">{children}</h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-lg font-semibold mb-3 mt-6 text-foreground">{children}</h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-base font-semibold mb-2 mt-4 text-foreground">{children}</h3>
                ),
                p: ({ children }) => (
                  <p className="text-base leading-7 mb-4 text-foreground/90">{children}</p>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc pl-5 space-y-2 mb-4">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal pl-5 space-y-2 mb-4">{children}</ol>
                ),
                li: ({ children }) => (
                  <li className="text-base leading-7 text-foreground/90">{children}</li>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-foreground">{children}</strong>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-primary/30 pl-4 italic text-muted-foreground my-4">
                    {children}
                  </blockquote>
                ),
              }}
            >
              {summary.content || ''}
            </ReactMarkdown>
          </div>
        </div>
      </div>

      {/* Action Bar - Bottom */}
      <div className="py-3 px-4 bg-background">
        <div className="flex flex-row items-center justify-between space-x-1">
          <div className="flex flex-row items-center space-x-1">
            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Copy to clipboard"
            >
              <Copy className="h-4 w-4" />
            </button>

            {/* Audio Button */}
            <button
              onClick={handleAudio}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Read aloud"
            >
              <Volume2 className="h-4 w-4" />
            </button>

            {/* Save as Note Button */}
            <button
              onClick={handleSaveNote}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Save as note"
            >
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 256 256" className="h-4 w-4" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                <path d="M84,108A12,12,0,0,1,96,96h64a12,12,0,0,1,0,24H96A12,12,0,0,1,84,108Zm32,28H96a12,12,0,0,0,0,24h20a12,12,0,0,0,0-24ZM228,48V156.69a19.86,19.86,0,0,1-5.86,14.14l-51.31,51.31A19.86,19.86,0,0,1,156.69,228H48a20,20,0,0,1-20-20V48A20,20,0,0,1,48,28H208A20,20,0,0,1,228,48ZM52,204h92V156a12,12,0,0,1,12-12h48V52H52Zm139-36H168v23Z"></path>
              </svg>
            </button>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Download"
            >
              <FileDown className="h-4 w-4" />
            </button>
          </div>

          {/* Copy confirmation text */}
          {copiedText && (
            <span className="text-xs text-correct animate-in fade-in-0">
              {copiedText}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarkdownSummaryViewer;
